/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, mergeMap } from 'rxjs/operators';
import { faCommentSlash } from '@fortawesome/free-solid-svg-icons/faCommentSlash';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons/faInfoCircle';
import { faList } from '@fortawesome/free-solid-svg-icons/faList';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons/faCheckCircle';
import { faCircle } from '@fortawesome/free-regular-svg-icons/faCircle';
import { faComment } from '@fortawesome/free-regular-svg-icons/faComment';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { MessageThreadsInterface } from '../../../models/interface/message_threads.interface';
import { AnswersheetInterface } from '../../../models/interface/answersheet.interface';
import { HelperFeedbackService } from '../../../services/helper/helper-feedback/helper-feedback.service';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { PaginationListComponent } from '../../utils/pagination-list/pagination-list.component';
import { MessageService } from 'src/app/services/api/message/message.service';
import { UserService } from 'src/app/services/api/user/user.service';
import { AnswersheetService } from 'src/app/services/api/answersheet/answersheet.service';

/**
 * Component:
 * Answersheets page displaying a list of answer sheets;
 * Can be found: {uri}/feedback-overview/answersheets
 */

@Component({
  selector: 'app-answersheets',
  templateUrl: './answersheets.component.html',
  styleUrls: ['./answersheets.component.scss'],
})
export class AnswersheetsComponent implements OnInit, OnDestroy {
  @ViewChild('paginator') paginator: PaginationListComponent;

  // Icons
  faList = faList;
  faInfoCircle = faInfoCircle;
  faComment = faComment;
  faCommentSlash = faCommentSlash;
  faCheckCircle = faCheckCircle;
  faCircle = faCircle;
  faEnvelope = faEnvelope;

  // Filter
  public sortCreation = ['answersheets.from_newest', 'answersheets.from_oldest'];
  public filter = {
    filterPendingActions: true,
    sortCreatedDate: this.sortCreation[0],
    isFBRequired: false,
    feedbackFilter: '2',
    searchFilter: '',
  };

  public isLoading$: Observable<boolean>;
  public pagedAnswersheets$: Observable<AnswersheetInterface[]>;
  public isCollapse$: Observable<boolean>;

  public users: UserInterface[] = [];
  public threads: MessageThreadsInterface[] = [];

  private answersheetIdToThread = new Map<string, MessageThreadsInterface>();
  private threadsWithNoAnswersheetId: MessageThreadsInterface[] = [];

  // Translation
  public param = { result_value: 0 };
  public interventionName = { intervention_name: '...' };

  public answersheetsContent$: Observable<AnswersheetInterface[]>;
  public answersheetsSubject = new BehaviorSubject<AnswersheetInterface[]>([]);

  public isCollapseSubject = new BehaviorSubject<boolean>(true);

  private answersheets: AnswersheetInterface[] = [];
  private initialAnswersheets: AnswersheetInterface[] = [];

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private pagedAnswersheetSubject = new BehaviorSubject<AnswersheetInterface[]>([]);
  private searchTextSubject = new BehaviorSubject<string>('');

  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private helperDialogService: HelperDialogService,
    private router: Router,
    private helperFeedbackService: HelperFeedbackService,
    private readonly messageService: MessageService,
    private readonly userService: UserService,
    private readonly answersheetService: AnswersheetService,
  ) {
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.pagedAnswersheets$ = this.pagedAnswersheetSubject.asObservable();
    this.isCollapse$ = this.isCollapseSubject.asObservable();
    this.answersheetsContent$ = this.answersheetsSubject.asObservable();
  }

  public get helper() {
    return this.helperService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  public get helperFeedback() {
    return this.helperFeedbackService;
  }

  ngOnInit(): void {
    this.applyFilter();
  }

  public applyFilter(): void {
    this.isLoadingSubject.next(true);
    this.subscriptions.push(
      this.reloadAnswersheetList().subscribe((_result) => {
        this.isLoadingSubject.next(false);
      })
    );
  }

  public updatePagedAnswersheets(event: any[]) {
    if (Array.isArray(event)) {
      this.pagedAnswersheetSubject.next(event);
    }
  }

  private getInboxMessagesWithAnswersheetId() {
    return this.messageService.getInboxFolder({
      operator: '>',
      answersheetId: 0,
      answersheetIdFlag: true,
    });
  }

  private getAnswersheetsOfInstances() {
    return this.answersheetService.getAllAnswersheetsOfAllEcoachInterventionInstances(
      { include: 'questionnaire,intervention_instance' },
    );
  }

  public reloadAnswersheetList(): Observable<boolean> {
    const reqs = {
      threads: this.getInboxMessagesWithAnswersheetId(),
      users: this.userService.getMyMembers(),
      answersheets: this.getAnswersheetsOfInstances(),
    };
    return forkJoin(reqs).pipe(
      mergeMap((results) => {
        this.setupThreadData(results.threads.body.data);
        this.users = results.users.body.data;
        this.answersheets = results.answersheets.body.data;
        if (this.filter['isFBRequired']) {
          this.answersheets = this.answersheets.filter(
            (answersheet) => this.isFeedbackRequired(answersheet),
          );
        }
        this.initialAnswersheets = this.answersheets;
        return this.performFilterAndPagination(true);
      }),
      catchError((_error) => {
        this.answersheets = [];
        this.initialAnswersheets = this.answersheets;
        return this.performFilterAndPagination(true);
      }),
    );
  }

  private setupThreadData(threads: MessageThreadsInterface[]) {
    this.threads = threads;
    this.answersheetIdToThread.clear();
    this.threadsWithNoAnswersheetId = [];
    for (let i = threads.length - 1; i >= 0; i--) {
      const thread = threads[i];
      if (thread.attributes.answersheet_id !== null) {
        const id = thread.attributes.answersheet_id.toString();
        this.answersheetIdToThread.set(id, thread);
      } else {
        this.threadsWithNoAnswersheetId.unshift(thread);
      }
    }
  }

  public getThreadByAnswersheet(answersheetId: number): MessageThreadsInterface | null {
    const id = answersheetId.toString();
    if (id === '') {
      return this.threadsWithNoAnswersheetId.length > 0 ? this.threadsWithNoAnswersheetId[0] : null;
    } else {
      const feedback = this.answersheetIdToThread.get(id);
      return feedback ? feedback : null;
    }
  }

  isFeedbackRequired(answersheet: AnswersheetInterface): boolean {
    const feedbackProvided = answersheet.relationships?.intervention_instances?.data.progress.feedback_provided;
    return this.helperFeedback.isFeedbackRequired2(answersheet.id, feedbackProvided);
  }

  isRead(answersheet: AnswersheetInterface): boolean {
    const feedbackProvided = answersheet.relationships?.intervention_instances?.data.progress.feedback_provided;
    const thread = this.getThreadByAnswersheet(answersheet.id);
    return this.helperFeedback.isRead2(answersheet.id, feedbackProvided, thread);
  }

  isUnread(answersheet: AnswersheetInterface): boolean {
    const feedbackProvided = answersheet.relationships?.intervention_instances?.data.progress.feedback_provided;
    const thread = this.getThreadByAnswersheet(answersheet.id);
    return this.helperFeedback.isUnread2(answersheet.id, feedbackProvided, thread);
  }

  hasUnreadMessaged(answersheet: AnswersheetInterface): boolean {
    const unread = this.getThreadByAnswersheet(answersheet.id)?.attributes.unread;
    return unread && unread.is_unread && unread.messages > 0;
  }

  hasUser(answersheet: AnswersheetInterface): boolean {
    const patientId = answersheet.relationships?.intervention_instances?.data.patient_id;
    return this.helper.findArrObjById(patientId, this.users) !== null;
  }

  getCodeNameEmail(answersheet: AnswersheetInterface): string {
    const studyId = answersheet.relationships?.intervention_instances?.data.study_id;
    return this.helper.getCodeNameEmail(answersheet.attributes.user_id, this.users, studyId);
  }

  public getAnswersheetDetails(id: number): void {
    this.router.navigateByUrl(`feedback-overview/answersheets/${id}`).then(() => {});
  }

  public openDialogInterventionInstanceDetails(instanceId: number): void {
    this.helperDialog
      .openDialogInterventionInstanceDetails(instanceId, this.param)
      .afterClosed()
      .subscribe(() => this.applyFilter());
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  identifyAnswersheet(_index: number, answersheet: AnswersheetInterface): number {
    return answersheet.id;
  }

  public performFilterAndPagination(setFirst: boolean): Observable<boolean> {
    return this.helper
      .searchAnswersheetsByInput(
        this.filter['searchFilter'],
        this.searchTextSubject,
        this.filterWithValues(),
        this.answersheetsSubject,
        this.users,
      )
      .pipe(
        mergeMap(() => {
          const currentPageSize = this.paginator.paginator.pageSize || 20;
          this.pagedAnswersheetSubject.next(this.answersheetsSubject.value.slice(0, currentPageSize));
          this.param = { result_value: this.answersheetsSubject.value.length };
          this.helper.setPagedContent(
            this.answersheetsSubject,
            this.pagedAnswersheetSubject,
            setFirst,
            this.paginator.paginator.pageIndex,
            currentPageSize,
          );
          return of(true);
        })
      );
  }

  private filterWithValues(): AnswersheetInterface[] {
    switch (this.filter['feedbackFilter']) {
      case '1': {
        this.initialAnswersheets = this.initialAnswersheets.filter((answersheet) => this.isUnread(answersheet));
        break;
      }
      case '0': {
        this.initialAnswersheets = this.initialAnswersheets.filter((answersheet) => this.isRead(answersheet));
        break;
      }
      default: {
        break;
      }
    }

    switch (this.filter['filterPendingActions']) {
      case true: {
        this.initialAnswersheets = this.initialAnswersheets.filter(
          (answersheet: AnswersheetInterface) =>
            (
              this.isFeedbackRequired(answersheet) &&
              !this.helperFeedback.hasFeedback(answersheet.id, this.threads)
            ) ||
            this.hasUnreadMessaged(answersheet),
        );
        break;
      }
      default: {
        break;
      }
    }

    switch (this.filter['sortCreatedDate']) {
      case 'answersheets.from_newest': {
        this.initialAnswersheets = this.helperService.getAnswersheetsByNewest(this.initialAnswersheets);
        break;
      }
      case 'answersheets.from_oldest': {
        this.initialAnswersheets = this.helperService.getAnswersheetsByOldest(this.initialAnswersheets);
        break;
      }
      default: {
        break;
      }
    }
    return this.initialAnswersheets;
  }
}
